import { syncTtsEchoMemory, useTtsPlaybackStore } from "./tts-playback-store";

const MIRRORED_ACTIVE_TTL_MS = 120_000;
type EchoTerminalState = "started" | "finished" | "cancelled" | "error";
// Single-slot `recent` only holds the currently playing utterance group, but a
// streamed reply speaks many groups in sequence. Transcripts of an earlier
// group would then classify as divergent user speech and leak into the
// conversation, so keep a small ring of everything spoken recently.
const MAX_ECHO_HISTORY = 8;
/** One state of a passage: when it began playing, and when it was noted. */
type EchoEntry = { text: string; startedAt: number; updatedAt: number };
let history: EchoEntry[] = [];

/** When the latest passage of `text` began, if it is remembered. */
function startOf(text: string): number | undefined {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (history[i]!.text === text) return history[i]!.startedAt;
  }
  return undefined;
}

export function rememberTtsEcho(text: string, state: EchoTerminalState): void {
  if (!text.trim()) return;
  syncTtsEchoMemory(text, state);
  const now = Date.now();
  const startedAt = state === "started" ? now : (startOf(text) ?? now);
  history = [...history, { text, startedAt, updatedAt: now }].slice(
    -MAX_ECHO_HISTORY,
  );
}

export function activeTtsEchoText(activeText?: string): string | null {
  if (activeText) return activeText;
  const mirrored = useTtsPlaybackStore.getState();
  if (
    mirrored.echoState !== "started" ||
    !mirrored.echoText ||
    !mirrored.echoUpdatedAt ||
    Date.now() - mirrored.echoUpdatedAt > MIRRORED_ACTIVE_TTL_MS
  ) {
    return null;
  }
  return mirrored.echoText;
}

/**
 * Every passage that played between `from` and `until`, the one playing now
 * first: each still playing or ended at `from` or later, none begun after
 * `until`. Another window's passage stands in when this one spoke none.
 */
export function spokenTtsEchoTexts(
  activeText: string | undefined,
  from: number,
  until: number,
): string[] {
  const texts: string[] = [];
  const active = activeTtsEchoText(activeText);
  if (active && (startOf(active) ?? mirroredStart(active)) <= until) {
    texts.push(active);
  }
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i]!;
    if (entry.updatedAt < from || entry.startedAt > until) continue;
    if (!texts.includes(entry.text)) texts.push(entry.text);
  }
  if (texts.length > 0) return texts;
  const mirrored = useTtsPlaybackStore.getState();
  if (!mirrored.echoText || !mirrored.echoUpdatedAt) return [];
  return mirrored.echoUpdatedAt >= from &&
    mirroredStart(mirrored.echoText) <= until
    ? [mirrored.echoText]
    : [];
}

/** When another window's passage of `text` began; -Infinity when unknown,
 *  so it is never ruled out for starting late. */
function mirroredStart(text: string): number {
  const mirrored = useTtsPlaybackStore.getState();
  return mirrored.echoText === text && mirrored.echoStartedAt !== null
    ? mirrored.echoStartedAt
    : -Infinity;
}

export function resetTtsEchoMemoryForTests(): void {
  history = [];
}
